import React from 'react';
import { SelectFieldComponent } from '../common/SelectFieldComponent';
import { useGrupos } from '../../hooks/useParametric';
import { type Grupo } from '../../types/catalog';

export interface GrupoSelectProps {
  /** Current selected value */
  value?: number | string;
  /** Change handler */
  onChange: (value: string | number) => void;
  /** Field label */
  label?: string;
  /** Error message */
  error?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Placeholder text */
  placeholder?: string;
  /** Custom filter function to filter grupos */
  filter?: (grupo: Grupo) => boolean;
  /** Custom sx prop for styling */
  sx?: any;
}

/**
 * GrupoSelect Component
 *
 * A reusable dropdown component that displays grupos from parametric data.
 * Features:
 * - Automatic data loading from parametric store
 * - Loading states and error handling
 * - Filtering capabilities
 * - Full TypeScript support
 * - Consistent styling with other form components
 */
export const GrupoSelect: React.FC<GrupoSelectProps> = ({
  value,
  onChange,
  label = 'Grupo',
  error,
  disabled = false,
  required = false,
  placeholder,
  filter,
  sx,
}) => {
  const { data: grupos, loading, fetch: fetchGrupos } = useGrupos();

  // Fetch grupos on component mount
  React.useEffect(() => {
    fetchGrupos();
  }, [fetchGrupos]);

  // Filter grupos based on props
  const filteredGrupos = React.useMemo(() => {
    let filtered = grupos;

    // Apply custom filter if provided
    if (filter) {
      filtered = filtered.filter(filter);
    }

    return filtered;
  }, [grupos, filter]);

  // Convert grupos to select options
  const options = React.useMemo(() => {
    return filteredGrupos.map((grupo) => ({
      value: grupo.idGrupo,
      label: grupo.nombre,
    }));
  }, [filteredGrupos]);

  // Handle error state
  const displayError = error;

  return (
    <SelectFieldComponent
      label={required ? `${label} *` : label}
      value={value || ''}
      onChange={onChange}
      options={options}
      error={displayError}
      disabled={disabled || loading}
      placeholder={placeholder}
      loading={loading}
      sx={sx}
    />
  );
};

export default GrupoSelect;